/**
 * UsageManagementPage.tsx
 * Owner 사용량 관리 페이지 - 청구서 조회, 사용량 분석, 클라우드 동기화 설정 통합
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  billingUsageRepository,
  SubscriptionInfo,
  AlimtalkUsage,
  CreditBalance,
  FailedNotificationLog,
} from '../repositories/BillingUsageRepository';
import AlimtalkBillsCard from './usage/AlimtalkBillsCard';
import UsageAnalyticsView from './usage/UsageAnalyticsView';
import CloudSyncTab from './usage/CloudSyncTab';

type TabType = 'bill' | 'analytics' | 'sync';

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
  },
  tabContainer: {
    display: 'flex',
    gap: '4px',
    marginBottom: '24px',
    borderBottom: '2px solid #e5e7eb',
    paddingBottom: '0',
  },
  tab: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    position: 'relative' as const,
    marginBottom: '-2px',
    transition: 'color 0.2s',
  },
  tabActive: {
    color: '#3b82f6',
    borderBottom: '2px solid #3b82f6',
  },
  planInfo: {
    backgroundColor: '#f0f9ff',
    borderRadius: '12px',
    padding: '16px 20px',
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
    border: '1px solid #bae6fd',
  },
  planName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#0369a1',
  },
  planDetails: {
    display: 'flex',
    gap: '16px',
    fontSize: '14px',
    color: '#0369a1',
    flexWrap: 'wrap' as const,
  },
  loading: {
    textAlign: 'center' as const,
    padding: '60px 20px',
    color: '#6b7280',
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  creditSection: {
    marginBottom: '24px',
  },
  creditCard: {
    backgroundColor: '#fefce8',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid #fde047',
  },
  creditHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  creditTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#854d0e',
  },
  chargeButton: {
    backgroundColor: '#eab308',
    color: '#ffffff',
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  creditGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
  },
  creditItem: {
    textAlign: 'center' as const,
  },
  creditLabel: {
    fontSize: '12px',
    color: '#a16207',
    marginBottom: '4px',
  },
  creditValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#854d0e',
  },
  creditValueSmall: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#854d0e',
  },
  lowBalanceWarning: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px 16px',
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  warningText: {
    fontSize: '14px',
    color: '#dc2626',
  },
  chargeModal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    width: '90%',
    maxWidth: '400px',
    maxHeight: '80vh',
    overflowY: 'auto' as const,
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  formLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '8px',
    color: '#374151',
  },
  formInput: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    boxSizing: 'border-box' as const,
  },
  amountButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
    flexWrap: 'wrap' as const,
  },
  amountButton: {
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  bankInfo: {
    backgroundColor: '#f3f4f6',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
  },
  bankInfoTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  bankInfoText: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.6,
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
  },
  modalButton: {
    flex: 1,
    padding: '12px',
    fontSize: '16px',
    fontWeight: '500',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
  },
  primaryButton: {
    backgroundColor: '#3b82f6',
    color: '#fff',
  },
  secondaryButton: {
    backgroundColor: '#e5e7eb',
    color: '#374151',
  },
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const generateMonthOptions = (count: number = 12): string[] => {
  const months: string[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('ko-KR').format(amount) + '원';
};

// 현재 청구 기간 계산 (매월 1일~말일 기준, 첫 달은 가입일~말일 일할 계산)
const getCurrentBillingPeriod = (startDate: string): { start: Date; end: Date; isPartialMonth: boolean } => {
  const subscriptionStart = new Date(startDate);
  const now = new Date();

  // 이번 달 1일 ~ 말일
  const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0); // 말일

  // 가입일이 이번 달이면 첫 달 (일할 계산)
  const isPartialMonth = subscriptionStart.getFullYear() === now.getFullYear()
    && subscriptionStart.getMonth() === now.getMonth()
    && subscriptionStart.getDate() > 1;

  if (isPartialMonth) {
    return { start: subscriptionStart, end: periodEnd, isPartialMonth: true };
  }

  return { start: periodStart, end: periodEnd, isPartialMonth: false };
};

// 일할 계산 비율 (첫 달이면 사용일수/총일수, 아니면 1)
const getProrationRate = (startDate: string): number => {
  const billingPeriod = getCurrentBillingPeriod(startDate);
  if (!billingPeriod.isPartialMonth) return 1;
  const { start, end } = billingPeriod;
  const daysInMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
  const usedDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  return usedDays / daysInMonth;
};

export const UsageManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('bill');
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [usage, setUsage] = useState<AlimtalkUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 크레딧 관련 상태
  const [credit, setCredit] = useState<CreditBalance | null>(null);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [chargeAmount, setChargeAmount] = useState<number>(10000);
  const [chargeLoading, setChargeLoading] = useState(false);

  // 실패 로그 상태
  const [failedLogs, setFailedLogs] = useState<FailedNotificationLog[]>([]);

  const availableMonths = generateMonthOptions(12);

  // 초기 데이터 로드 (마운트 시 한 번만)
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const currentMonth = getCurrentMonth();

      const [subData, usageData, creditData, failedData] = await Promise.all([
        billingUsageRepository.getMySubscription(),
        billingUsageRepository.getUsage(currentMonth),
        billingUsageRepository.getCreditBalance().catch(() => null),
        billingUsageRepository.getRecentFailedLogs(30, 20).catch(() => []),
      ]);

      console.log('[UsageManagement] subscription data:', subData);
      setSubscription(subData);
      setUsage(usageData);
      setCredit(creditData);
      setFailedLogs(failedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : '데이터를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  }, []); // 의존성 없음 - 마운트 시 한 번만 실행

  // 크레딧 잔액 새로고침
  const refreshCredit = useCallback(async () => {
    try {
      const creditData = await billingUsageRepository.getCreditBalance();
      setCredit(creditData);
    } catch (err) {
      console.error('크레딧 조회 실패:', err);
    }
  }, []);

  // 충전 요청 핸들러
  const handleChargeRequest = async () => {
    if (chargeAmount < 1000) {
      alert('최소 충전 금액은 1,000원입니다.');
      return;
    }

    try {
      setChargeLoading(true);
      await billingUsageRepository.requestCharge(chargeAmount, 'bank_transfer');
      alert(`${formatCurrency(chargeAmount)} 충전 요청이 접수되었습니다.\n입금 확인 후 크레딧이 충전됩니다.`);
      setShowChargeModal(false);
      setChargeAmount(10000);
    } catch (err) {
      alert(err instanceof Error ? err.message : '충전 요청에 실패했습니다.');
    } finally {
      setChargeLoading(false);
    }
  };

  // 월 변경 시 데이터 로드
  const loadMonthData = useCallback(async (month: string) => {
    try {
      setUsageLoading(true);
      setError(null);

      const usageData = await billingUsageRepository.getUsage(month);
      setUsage(usageData);
    } catch (err) {
      setError(err instanceof Error ? err.message : '데이터를 불러오는데 실패했습니다');
    } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    loadMonthData(month);
  };

  if (loading) {
    return <div style={styles.loading}>데이터를 불러오는 중...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>사용량 관리</h1>
        <p style={styles.subtitle}>
          청구서 조회 및 알림톡 사용량을 분석할 수 있습니다
        </p>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* 현재 플랜 정보 */}
      {subscription && (
        <div style={styles.planInfo}>
          <div style={styles.planName}>
            {subscription.plan_name} 플랜
            {subscription.has_custom_values && ' (커스텀)'}
          </div>
          {(() => {
            const billingPeriod = subscription.start_date
              ? getCurrentBillingPeriod(subscription.start_date)
              : null;
            const prorationRate = billingPeriod?.isPartialMonth
              ? (() => {
                  const start = billingPeriod.start;
                  const end = billingPeriod.end;
                  const daysInMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
                  const usedDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                  return usedDays / daysInMonth;
                })()
              : 1;
            const proratedFee = Math.round(subscription.monthly_base_fee * prorationRate / 100) * 100;
            const proratedFreeQuota = Math.floor(subscription.alimtalk_free_quota * prorationRate);

            return (
              <>
                <div style={styles.planDetails}>
                  {billingPeriod?.isPartialMonth ? (
                    <>
                      <span>기본료: {formatCurrency(proratedFee)} <span style={{ fontSize: '12px', color: '#6b7280' }}>({formatCurrency(subscription.monthly_base_fee)}의 일할)</span></span>
                      <span>알림톡: {subscription.alimtalk_unit_price}원/건</span>
                      <span>무료: {proratedFreeQuota.toLocaleString()}건 <span style={{ fontSize: '12px', color: '#6b7280' }}>({subscription.alimtalk_free_quota.toLocaleString()}건의 일할)</span></span>
                    </>
                  ) : (
                    <>
                      <span>기본료: {formatCurrency(subscription.monthly_base_fee)}</span>
                      <span>알림톡: {subscription.alimtalk_unit_price}원/건</span>
                      <span>무료: {subscription.alimtalk_free_quota.toLocaleString()}건</span>
                    </>
                  )}
                </div>
                {billingPeriod && (
                  <div style={{ marginTop: '8px', fontSize: '14px', color: '#0369a1' }}>
                    <span style={{ fontWeight: 500 }}>
                      청구기간: {billingPeriod.start.toLocaleDateString('ko-KR')} ~ {billingPeriod.end.toLocaleDateString('ko-KR')}
                      {billingPeriod.isPartialMonth && ' (일할 계산)'}
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* 과금 정보 - billing_type에 따라 분기 */}
      {subscription?.billing_type === 'prepaid' ? (
        /* 선불제: 기존 크레딧 UI */
        credit && (
          <div style={styles.creditSection}>
            <div style={styles.creditCard}>
              <div style={styles.creditHeader}>
                <div style={styles.creditTitle}>알림톡 크레딧 (선불제)</div>
                <button style={styles.chargeButton} onClick={() => setShowChargeModal(true)}>충전하기</button>
              </div>
              <div style={styles.creditGrid}>
                <div style={styles.creditItem}>
                  <div style={styles.creditLabel}>보유 크레딧</div>
                  <div style={styles.creditValue}>{formatCurrency(credit.balance)}</div>
                </div>
                <div style={styles.creditItem}>
                  <div style={styles.creditLabel}>무료 잔여</div>
                  <div style={styles.creditValueSmall}>{credit.free_remaining.toLocaleString()} / {credit.free_quota.toLocaleString()}건</div>
                </div>
                <div style={styles.creditItem}>
                  <div style={styles.creditLabel}>예상 발송 가능</div>
                  <div style={styles.creditValueSmall}>
                    {subscription ? (credit.free_remaining + Math.floor(credit.balance / subscription.alimtalk_unit_price)).toLocaleString() : credit.free_remaining.toLocaleString()}건
                  </div>
                </div>
              </div>
              {credit.balance < 5000 && credit.free_remaining === 0 && (
                <div style={styles.lowBalanceWarning}>
                  <span style={styles.warningText}>크레딧 잔액이 부족합니다. 충전 후 알림톡을 발송할 수 있습니다.</span>
                </div>
              )}
            </div>
          </div>
        )
      ) : (
        /* 후불제: 사용량 현황 UI */
        credit && subscription && (
          <div style={styles.creditSection}>
            <div style={styles.creditCard}>
              <div style={styles.creditHeader}>
                <div style={styles.creditTitle}>이번달 알림톡 현황 (후불제)</div>
              </div>
              {/* 사용량 바 */}
              <div style={{marginBottom: '16px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '6px'}}>
                  <span style={{color: '#6b7280'}}>사용량</span>
                  <span style={{fontWeight: 'bold'}}>
                    {(credit.free_quota - credit.free_remaining).toLocaleString()} / {credit.free_quota.toLocaleString()}건
                  </span>
                </div>
                <div style={{width: '100%', backgroundColor: '#e5e7eb', borderRadius: '8px', height: '16px', overflow: 'hidden'}}>
                  <div style={{
                    width: `${Math.min(((credit.free_quota - credit.free_remaining) / credit.free_quota) * 100, 100)}%`,
                    height: '100%',
                    borderRadius: '8px',
                    backgroundColor: (credit.free_quota - credit.free_remaining) > credit.free_quota ? '#ef4444' :
                      (credit.free_quota - credit.free_remaining) > credit.free_quota * 0.8 ? '#f97316' : '#22c55e',
                    transition: 'width 0.3s',
                  }} />
                </div>
                {credit.free_remaining === 0 && (
                  <div style={{fontSize: '12px', color: '#ef4444', fontWeight: 'bold', marginTop: '4px', textAlign: 'right'}}>
                    무료 한도 소진 - 초과분 월말 정산
                  </div>
                )}
              </div>
              {/* 예상 청구 요약 */}
              <div style={{backgroundColor: '#f9fafb', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px'}}>
                  <span style={{color: '#6b7280'}}>월 이용료</span>
                  <span>{formatCurrency(subscription.monthly_base_fee || subscription.price || 0)}</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px'}}>
                  <span style={{color: '#6b7280'}}>알림톡 초과</span>
                  <span style={{color: credit.free_remaining === 0 ? '#ef4444' : '#6b7280'}}>
                    {credit.free_remaining === 0 ? `+${formatCurrency(Math.ceil((credit.free_quota - credit.free_remaining - credit.free_quota) * subscription.alimtalk_unit_price))}` : '0원'}
                  </span>
                </div>
                <div style={{borderTop: '1px solid #e5e7eb', paddingTop: '6px', marginTop: '4px'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280'}}>
                    <span>소계</span>
                    <span>{formatCurrency(subscription.monthly_base_fee || subscription.price || 0)}</span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280'}}>
                    <span>VAT (10%)</span>
                    <span>{formatCurrency(Math.round((subscription.monthly_base_fee || subscription.price || 0) * 0.1))}</span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 'bold', color: '#1d4ed8', marginTop: '4px'}}>
                    <span>예상 청구액 (VAT 포함)</span>
                    <span>{formatCurrency(Math.round((subscription.monthly_base_fee || subscription.price || 0) * 1.1))}</span>
                  </div>
                </div>
              </div>
              <div style={{fontSize: '12px', color: '#9ca3af', marginTop: '8px', textAlign: 'center'}}>
                후불 정산: 매월 말 청구서가 발행됩니다
              </div>
            </div>
          </div>
        )
      )}

      {/* 탭 */}
      <div style={styles.tabContainer}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'bill' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('bill')}
        >
          청구서 조회
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'analytics' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('analytics')}
        >
          사용량 분석
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'sync' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('sync')}
        >
          동기화 설정
        </button>
      </div>

      {/* 탭 컨텐츠 */}
      {activeTab === 'bill' ? (
        <>
        {/* 실제 청구 근거. 종전엔 여기 아래에 플랜 기반 청구서 화면이 하나 더 있었으나
            어느 매장에도 플랜이 없어 늘 "청구서 정보가 없습니다"만 떴다. 원장이 자기
            청구서가 없는 줄 오해해서 걷어냈다(2026-09-08 대표 결정). */}
        <AlimtalkBillsCard />
        </>
      ) : activeTab === 'analytics' ? (
        <UsageAnalyticsView
          usage={usage}
          freeQuota={subscription?.start_date
            ? Math.floor((subscription?.alimtalk_free_quota || 0) * getProrationRate(subscription.start_date))
            : (subscription?.alimtalk_free_quota || 0)}
          unitPrice={subscription?.alimtalk_unit_price || 13}
          failedLogs={failedLogs}
          selectedMonth={selectedMonth}
          availableMonths={availableMonths}
          onMonthChange={handleMonthChange}
          loading={usageLoading}
        />
      ) : (
        <CloudSyncTab />
      )}

      {/* 충전 요청 모달 */}
      {showChargeModal && (
        <div style={styles.chargeModal} onClick={() => setShowChargeModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>크레딧 충전</h2>

            <div style={styles.bankInfo}>
              <div style={styles.bankInfoTitle}>입금 계좌 안내</div>
              <div style={styles.bankInfoText}>
                국민은행 000000-00-000000<br />
                예금주: (주)NewAttend<br /><br />
                입금자명에 <strong>독서실명</strong>을 기재해주세요.
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>충전 금액</label>
              <input
                type="number"
                style={styles.formInput}
                value={chargeAmount}
                onChange={(e) => setChargeAmount(Math.max(0, parseInt(e.target.value) || 0))}
                min={1000}
                step={1000}
              />
              <div style={styles.amountButtons}>
                {[10000, 30000, 50000, 100000].map((amount) => (
                  <button
                    key={amount}
                    style={styles.amountButton}
                    onClick={() => setChargeAmount(amount)}
                  >
                    {(amount / 10000).toLocaleString()}만원
                  </button>
                ))}
              </div>
            </div>

            {chargeAmount > 0 && (
              <>
                {/* 부가세 포함 입금액 안내 */}
                <div style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '8px' }}>
                    <strong>입금 금액 (부가세 포함)</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#78350f', marginBottom: '4px' }}>
                    <span>크레딧 충전액</span>
                    <span>{chargeAmount.toLocaleString()}원</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#78350f', marginBottom: '8px' }}>
                    <span>부가세 (10%)</span>
                    <span>{Math.floor(chargeAmount * 0.1).toLocaleString()}원</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#92400e',
                    borderTop: '1px solid #f59e0b',
                    paddingTop: '8px'
                  }}>
                    <span>총 입금액</span>
                    <span>{Math.floor(chargeAmount * 1.1).toLocaleString()}원</span>
                  </div>
                </div>

                {/* 예상 발송 가능 건수 */}
                {subscription && (
                  <div style={{ ...styles.bankInfo, backgroundColor: '#f0fdf4' }}>
                    <div style={{ fontSize: '14px', color: '#166534' }}>
                      예상 발송 가능: 약 {Math.floor(chargeAmount / subscription.alimtalk_unit_price).toLocaleString()}건
                    </div>
                  </div>
                )}
              </>
            )}

            <div style={styles.modalButtons}>
              <button
                style={{ ...styles.modalButton, ...styles.secondaryButton }}
                onClick={() => setShowChargeModal(false)}
                disabled={chargeLoading}
              >
                취소
              </button>
              <button
                style={{ ...styles.modalButton, ...styles.primaryButton }}
                onClick={handleChargeRequest}
                disabled={chargeLoading || chargeAmount < 1000}
              >
                {chargeLoading ? '처리중...' : '충전 요청'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageManagementPage;
